import React, { useState, useCallback, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styles from './styles/file-input.module.css';
import sharedStyles from './styles/shared-components.module.css';
import './styles/file-input.css';
import { Button, Upload, Tooltip, DatePicker } from 'antd';
import { SendOutlined, PaperClipOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { addAnswer } from '@/store/slices/chatbotFlow';
import { getRemainingUploadBytes } from '@/helpers/UploadBudget';
import { MAX_FILE_SIZE_BYTES } from '@/constants/UploadLimits';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

const FileChip = ({ file, onRemove }) => (
  <div className={styles.file_chip}>
    <span className={styles.file_chip_name} title={file.name}>{file.name}</span>
    <Button
      className={styles.file_chip_remove}
      type="text"
      size="small"
      onClick={() => onRemove(file)}
      icon={<CloseOutlined style={{ fontSize: '10px' }} />}
    />
  </div>
);

FileChip.propTypes = {
  file: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const AddFileButton = ({ beforeUpload, onClick }) => (
  <Upload beforeUpload={beforeUpload} showUploadList={false}>
    <Tooltip title="Add another file" placement="top">
      <Button
        onClick={onClick}
        className={styles.add_file_button}
        type="text"
        size="small"
        icon={<PlusOutlined style={{ fontSize: '14px' }} />}
      />
    </Tooltip>
  </Upload>
);

AddFileButton.propTypes = {
  beforeUpload: PropTypes.func.isRequired,
  onClick: PropTypes.func,
};

const FileInput = ({ addAnswer, isChatbotVisible, animTransition, block, remainingUploadBytes }) => {
  const { t } = useTranslation();

  const [fileList, setFileList] = useState([]);
  const [base64Files, setBase64Files] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [expiryDates, setExpiryDates] = useState([]);
  
  const totalSizeRef = useRef(0);

  useEffect(() => {
    if (!isChatbotVisible) {
      setShowError(false);
      setErrorMessage('');
    }
  }, [isChatbotVisible]);

  const allowMultiple = block?.options?.allowMultiple || false;
  const hasExpirationDate = block?.options?.hasExpirationDate || false;
  const allowedTypes = block?.options?.types || [];
  const isFileSelected = fileList.length > 0;

  const handleFileLoad = useCallback((file, result) => {
    if (allowMultiple) {
      setFileList(prev => [...prev, file]);
      setBase64Files(prev => [...prev, result]);
      setExpiryDates(prev => [...prev, null]);
    } else {
      totalSizeRef.current = file.size;
      setFileList([file]);
      setBase64Files([result]);
      setExpiryDates([null]);
    }
    setLoading(false);
  }, [allowMultiple]);

  const handleFileError = useCallback((message) => {
    setShowError(true);
    setErrorMessage(message);
    setLoading(false);
  }, []);

  const buildFileAnswer = useCallback((file, index) => {
    const answer = { name: file.name, data: base64Files[index] };
    if (hasExpirationDate && expiryDates[index]) {
      answer.expiryDate = dayjs(expiryDates[index]).format('YYYY-MM-DD');
    }
    return answer;
  }, [base64Files, hasExpirationDate, expiryDates]);

  const validateExpiryDates = useCallback(() => {
    if (!hasExpirationDate) return true;
    const missingIndex = expiryDates.findIndex(d => !d);
    if (missingIndex === -1) return true;
    setShowError(true);
    setErrorMessage(`Please select an expiry date for "${fileList[missingIndex].name}".`);
    return false;
  }, [hasExpirationDate, expiryDates, fileList]);

  const submitInput = useCallback(() => {
    if (!isFileSelected || !validateExpiryDates()) return;
    const answer = allowMultiple
      ? fileList.map((file, index) => buildFileAnswer(file, index))
      : buildFileAnswer(fileList[0], 0);
    addAnswer(answer);
  }, [isFileSelected, fileList, addAnswer, allowMultiple, validateExpiryDates, buildFileAnswer]);

  const validateFile = useCallback((file) => {
    if (allowedTypes.length) {
      const extension = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : '';
      if (!allowedTypes.some(type => type.toLowerCase() === extension)) {
        return t('fileInputErrors.unsupportedFileType', { types: allowedTypes.join(', ') });
      }
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return t('fileInputErrors.fileTooLarge');
    }
    // `remainingUploadBytes` already accounts for files attached in earlier blocks,
    // so only the files selected in this block have to be added here.
    const pendingSize = allowMultiple ? totalSizeRef.current : 0;
    if (pendingSize + file.size > remainingUploadBytes) {
      return t('fileInputErrors.conversationTooLarge');
    }
    return null;
  }, [allowMultiple, allowedTypes, remainingUploadBytes, t]);

  const processFile = useCallback((file) => {
    totalSizeRef.current += file.size;
    setLoading(true);
    const reader = new FileReader();
    reader.onload = e => handleFileLoad(file, e.target.result);
    reader.onerror = () => {
      totalSizeRef.current = Math.max(0, totalSizeRef.current - file.size);
      handleFileError(t('fileInputErrors.readError'));
    };
    reader.readAsDataURL(file);
  }, [handleFileLoad, handleFileError, t]);

  const clearError = useCallback(() => {
    setShowError(false);
    setErrorMessage('');
  }, []);

  const beforeUpload = useCallback((file) => {
    setShowError(false);
    setErrorMessage('');
    const validationError = validateFile(file);
    if (validationError) {
      handleFileError(validationError);
      return Upload.LIST_IGNORE;
    }
    processFile(file);
    return false;
  }, [validateFile, processFile, handleFileError]);

  const updateExpiryDate = useCallback((index, date) => {
    setExpiryDates(prev => {
      const updated = [...prev];
      updated[index] = date;
      return updated;
    });
    setShowError(false);
    setErrorMessage('');
  }, []);

  const onRemove = useCallback((fileToRemove) => {
    totalSizeRef.current = Math.max(0, totalSizeRef.current - (fileToRemove?.size || 0));
    setShowError(false);
    setErrorMessage('');
    if (allowMultiple) {
      const index = fileList.indexOf(fileToRemove);
      if (index > -1) {
        setFileList(prev => prev.filter((_, i) => i !== index));
        setBase64Files(prev => prev.filter((_, i) => i !== index));
        setExpiryDates(prev => prev.filter((_, i) => i !== index));
      }
    } else {
      setFileList([]);
      setBase64Files([]);
      setExpiryDates([]);
    }
  }, [fileList, allowMultiple]);

  const renderFileList = () => {
    if (hasExpirationDate) {
      return (
        <div className={styles.file_rows_container}>
          {fileList.map((file, index) => (
            <div key={index} className={styles.file_row}>
              <FileChip file={file} onRemove={onRemove} />
              <DatePicker
                className={styles.file_row_date_picker}
                placeholder="Expiry date"
                format="YYYY-MM-DD"
                size="small"
                onChange={(date) => updateExpiryDate(index, date)}
                value={expiryDates[index]}
                disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
              />
            </div>
          ))}
          {allowMultiple && <AddFileButton beforeUpload={beforeUpload} onClick={clearError} />}
        </div>
      );
    }
    return (
      <div className={styles.file_chips_container}>
        {fileList.map((file, index) => (
          <FileChip key={index} file={file} onRemove={onRemove} />
        ))}
        {allowMultiple && <AddFileButton beforeUpload={beforeUpload} onClick={clearError} />}
      </div>
    );
  };

  return (
    <div className={sharedStyles.wrapper}>
      <div className={styles.input_container}>
        {isFileSelected ? (
          <div className={styles.file_display}>
            {renderFileList()}
          </div>
        ) : (
          <Upload
            beforeUpload={beforeUpload}
            showUploadList={false}
            multiple={allowMultiple}
          >
            <span className={styles.placeholder} onClick={clearError}>Click here to select {allowMultiple ? 'files' : 'a file'}</span>
          </Upload>
        )}
      </div>
      <div className={styles.buttons}>
        <Tooltip
          placement="topRight"
          align={{ offset: [-5, -5] }}
          title={errorMessage}
          open={showError && isChatbotVisible && animTransition}
        >
          <Button
            className={sharedStyles.send_button}
            loading={loading}
            onClick={submitInput}
            type="text"
            icon={
              isFileSelected ? (
                <SendOutlined style={{ fontSize: '24px' }} />
              ) : (
                <Upload
                  beforeUpload={beforeUpload}
                  showUploadList={false}
                >
                  <span onClick={clearError}>
                    <PaperClipOutlined style={{ fontSize: '24px' }} />
                  </span>
                </Upload>
              )
            }
          />
        </Tooltip>
      </div>
    </div>
  );
};

FileInput.propTypes = {
  addAnswer: PropTypes.func.isRequired,
  isChatbotVisible: PropTypes.bool.isRequired,
  animTransition: PropTypes.bool.isRequired,
  block: PropTypes.object,
  remainingUploadBytes: PropTypes.number.isRequired,
};

const mapStateToProps = state => ({
  isChatbotVisible: state.chatbotVisibility.isVisible,
  animTransition: state.chatbotVisibility.animTransition,
  remainingUploadBytes: getRemainingUploadBytes(state.chatbotFlow.conversation),
});

const mapDispatchToProps = { addAnswer };

export default connect(mapStateToProps, mapDispatchToProps)(FileInput);
